import { Graphics } from "pixi.js";
import type { ShipSnapshot } from "@frontier/protocol/contracts";
import type { Point } from "./geometry";
import {
  bossFlakFan,
  npcAttackGeometry,
  npcAttackVisible,
} from "./npc-attack-geometry";

export class NpcTelegraph {
  readonly graphics = new Graphics();
  render(
    state: ShipSnapshot,
    position: Point,
    now: number,
    receivedAt: number,
  ): void {
    const drawing = this.graphics.clear();
    if (!npcAttackVisible(state, (now - receivedAt) / 1000)) return;
    const repairing = state.attackKind === "repair";
    const { endpoint, radius } = npcAttackGeometry(state);
    const x = endpoint.x - position.x;
    const y = endpoint.y - position.y;
    const color = repairing
      ? 0x70e7a2
      : state.attackKind === "emp"
        ? 0xc69bff
        : state.attackKind === "missile-salvo"
          ? 0xffca70
          : 0xff7669;
    const alpha = 0.6 + Math.sin(now / 75) * 0.18;
    const fan = bossFlakFan(state);
    if (fan) {
      drawing
        .moveTo(0, 0)
        .arc(
          0,
          0,
          fan.range,
          fan.angle - fan.halfAngle,
          fan.angle + fan.halfAngle,
        )
        .lineTo(0, 0)
        .fill({ color, alpha: 0.07 })
        .stroke({ color, width: 2, alpha });
      for (let index = -3; index <= 3; index++) {
        const angle = fan.angle + (fan.halfAngle * index) / 3;
        drawing
          .moveTo(0, 0)
          .lineTo(Math.cos(angle) * fan.range, Math.sin(angle) * fan.range)
          .stroke({ color, width: 1, alpha: alpha * 0.25 });
      }
      return;
    }
    if (repairing) {
      drawing
        .moveTo(0, 0)
        .lineTo(x, y)
        .stroke({ color, width: 6, alpha: 0.13 });
      drawing
        .moveTo(0, 0)
        .lineTo(x, y)
        .stroke({ color, width: 2, alpha: 0.75 });
      drawing
        .circle(x, y, 11 + Math.sin(now / 150) * 3)
        .stroke({ color, width: 2, alpha: 0.7 });
      return;
    }
    if (state.attackKind === "barrage") {
      drawing
        .circle(x, y, radius)
        .fill({ color, alpha: 0.1 })
        .stroke({ color, width: 3, alpha });
      drawing
        .moveTo(x - 18, y)
        .lineTo(x + 18, y)
        .moveTo(x, y - 18)
        .lineTo(x, y + 18)
        .stroke({ color, width: 2, alpha });
      return;
    }
    if (state.attackKind === "charge") {
      drawing
        .moveTo(0, 0)
        .lineTo(x, y)
        .stroke({ color, width: state.radius * 2, alpha: 0.1 });
      drawing.moveTo(0, 0).lineTo(x, y).stroke({ color, width: 3, alpha });
      const angle = Math.atan2(y, x);
      drawing
        .poly([
          x - Math.cos(angle - 0.55) * 28,
          y - Math.sin(angle - 0.55) * 28,
          x,
          y,
          x - Math.cos(angle + 0.55) * 28,
          y - Math.sin(angle + 0.55) * 28,
        ])
        .stroke({ color, width: 3, alpha });
      return;
    }
    drawing
      .moveTo(0, 0)
      .lineTo(x, y)
      .stroke({ color, width: 1.5, alpha: alpha * 0.6 });
    drawing
      .circle(x, y, state.attackKind === "missile-salvo" ? 22 : 14)
      .stroke({ color, width: 2, alpha });
    drawing
      .moveTo(x - 22, y)
      .lineTo(x - 10, y)
      .moveTo(x + 10, y)
      .lineTo(x + 22, y)
      .moveTo(x, y - 22)
      .lineTo(x, y - 10)
      .moveTo(x, y + 10)
      .lineTo(x, y + 22)
      .stroke({ color, width: 2, alpha });
  }
}
